import React, {useCallback, useEffect, useMemo, useRef} from "react";
import raf from "raf";
import Immutable from "immutable";

export const StickyContext = React.createContext({
    subscribe: null,
    unsubscribe: null,
    getParent: null,
});

const eventTypes = new Set([
    "resize",
    "scroll",
    "touchstart",
    "touchmove",
    "touchend",
    "pageshow",
    "load"
]);

function useAnimationFrames() {
    const rafHandle = useRef(null);
    const framePending = useRef(false);
    useEffect(() => {
        return () => {
            if (rafHandle.current) {
                raf.cancel(rafHandle.current);
                rafHandle.current = null;
            }
        }
    }, []);
    return useCallback(action => {
        if (!framePending.current) {
            rafHandle.current = raf(() => {
                framePending.current = false;
                rafHandle.current = null;
                action();
            });
            framePending.current = true;
        }
    }, [rafHandle, framePending]);
}

const StickyContainer = (props) => {
    const subscribers = useRef(Immutable.List());
    const node = useRef(null);
    const requestFrame = useAnimationFrames();
    const notifySubscribers = useCallback(({currentTarget}) => {
        requestFrame(() => {
            const {top, bottom} = node.current.getBoundingClientRect();

            subscribers.current.forEach(handler =>
                handler({
                    distanceFromTop: top,
                    distanceFromBottom: bottom,
                    eventSource: currentTarget === window ? document.body : node.current
                })
            );
        });
    }, [subscribers]);
    useEffect(() => {
        for (const eventType of eventTypes) {
            window.addEventListener(eventType, notifySubscribers);
        }
        return () => {
            for (const eventType of eventTypes) {
                window.removeEventListener(eventType, notifySubscribers);
            }
        };
    }, [notifySubscribers]);
    const subscribe = useCallback(handler => {
        subscribers.current = subscribers.current.push(handler);
    }, [subscribers]);
    const unsubscribe = useCallback(handler => {
        subscribers.current = subscribers.current.filter(it => it !== handler);
    }, [subscribers]);
    const contextValue = useMemo(() => {
        return {
            subscribe,
            unsubscribe,
            get parent() {
                return node.current;
            },
            get subscribers() { // visible for testing
                return subscribers.current.toJS();
            },
            get node() { // visible for testing
                return node.current;
            },
            set node(testNode) { // visible for testing
                node.current = testNode;
            },
            notifySubscribers // visible for testing
        };
    }, [subscribe, unsubscribe, node, subscribers, notifySubscribers]);

    return (
        <StickyContext.Provider value={contextValue}>
            <div
                {...props}
                ref={node}
                onScroll={notifySubscribers}
                onTouchStart={notifySubscribers}
                onTouchMove={notifySubscribers}
                onTouchEnd={notifySubscribers}
            />
        </StickyContext.Provider>
    );
};

export default React.memo(StickyContainer);
